import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../../lib/api";
import { Card, Btn } from "../../components/UI";
import { ShieldCheck, Zap, Loader2, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { AutopilotActivity } from "./AutopilotActivity";

const AUTHORITY = [
  { value: "recommend_only", label: "Recommend only", desc: "Every change needs your approval." },
  { value: "guarded_autopilot", label: "Guarded autopilot", desc: "AI adjusts pacing, frequency & creative within the limits you set below." },
  { value: "managed_autopilot", label: "Managed autopilot", desc: "Team & AI act within your approved scope. Compliance approvals stay mandatory." },
];

const SERVICE = [
  { value: "self_serve", label: "Build It Myself", desc: "You control audiences, media, creative, budgets and settings." },
  { value: "ai_builds_approve", label: "AI Builds — I Approve", desc: "AI builds campaigns; nothing launches without your approval.", recommended: true },
  { value: "managed", label: "Managed Campaign", desc: "TotalReach AI + your authorized team manage within your mandate." },
];

const NUM_FIELDS = [
  { key: "max_daily_budget_change_pct", label: "Max daily budget change", suffix: "%" },
  { key: "max_channel_reallocation_pct", label: "Max channel reallocation", suffix: "%" },
];

const BOOL_FIELDS = [
  { key: "allow_pacing_adjustment", label: "AI may adjust pacing" },
  { key: "allow_bid_adjustment", label: "AI may adjust bids within approved ranges" },
  { key: "allow_frequency_adjustment", label: "AI may adjust frequency caps" },
  { key: "allow_creative_rotation", label: "AI may rotate approved creatives" },
  { key: "require_approval_for_new_creative", label: "New creative always requires approval" },
  { key: "require_approval_for_targeting_change", label: "Targeting changes always require approval" },
  { key: "require_approval_for_message_change", label: "Message changes always require approval" },
];

export default function AutopilotSettings() {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [authority, setAuthority] = useState("recommend_only");
  const [service, setService] = useState("ai_builds_approve");
  const [perms, setPerms] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/ux/prefs").then(({ data }) => {
      setPrefs(data);
      if (data.ai_authority) setAuthority(data.ai_authority);
      if (data.service_mode) setService(data.service_mode);
      setPerms(data.ai_permissions || {});
    }).catch(() => setPrefs({}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/ux/experience", { service_mode: service, ai_authority: authority, ai_permissions: perms });
      toast.success("Autopilot settings saved");
    } catch (e) { toast.error(errMsg(e, "Could not save settings")); }
    finally { setSaving(false); }
  };

  if (!prefs) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  if (!prefs.entity_id) {
    return (
      <div className="p-6 lg:p-10 max-w-3xl mx-auto" data-testid="autopilot-no-entity">
        <Card>
          <p className="text-sm text-slate-400 mb-4">Set up your political entity first — autopilot limits are saved per campaign workspace.</p>
          <button onClick={() => navigate("/onboarding/political")} className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold rounded-full px-5 py-2.5 text-sm hover:opacity-90">
            Get set up <ArrowRight className="w-4 h-4" />
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto" data-testid="autopilot-settings">
      <div className="text-[11px] uppercase tracking-widest text-cyan-300 font-semibold mb-2">Agent Autopilot</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">How much should TotalReach manage for you?</h1>
      <p className="text-sm text-slate-400 mb-8 max-w-2xl">
        Set the limits AI must respect — every change is logged, reversible, and political compliance approvals always remain mandatory.
      </p>

      <div className="mb-8" data-testid="autopilot-authority-section">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-cyan-300" />
          <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-400">AI authority</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          {AUTHORITY.map((a) => (
            <button key={a.value} onClick={() => setAuthority(a.value)} data-testid={`autopilot-authority-${a.value}`}
              className={`text-left p-4 rounded-xl border transition-colors ${authority === a.value ? "border-cyan-400/60 bg-cyan-400/10" : "border-white/10 hover:border-cyan-400/40 bg-white/[0.02]"}`}>
              <div className="text-sm font-semibold mb-1.5 flex items-center gap-2">
                {a.label} {authority === a.value && <Check className="w-3.5 h-3.5 text-cyan-300" />}
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">{a.desc}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="mb-8" data-testid="autopilot-service-section">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4 text-cyan-300" />
          <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-400">Service mode</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          {SERVICE.map((s) => (
            <button key={s.value} onClick={() => setService(s.value)} data-testid={`autopilot-service-${s.value}`}
              className={`relative text-left p-4 rounded-xl border transition-colors ${service === s.value ? "border-cyan-400/60 bg-cyan-400/10" : "border-white/10 hover:border-cyan-400/40 bg-white/[0.02]"}`}>
              {s.recommended && <span className="absolute top-2 right-2 text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-400/20 border border-cyan-400/30 text-cyan-300 uppercase tracking-wider font-bold">Recommended</span>}
              <div className="text-sm font-semibold mb-1.5 pr-4">{s.label}</div>
              <p className="text-[11px] text-slate-400 leading-relaxed">{s.desc}</p>
            </button>
          ))}
        </div>
      </div>

      <Card className={`mb-8 ${authority === "recommend_only" ? "opacity-60" : ""}`} data-testid="autopilot-limits-card">
        <h3 className="font-display text-lg font-bold mb-1">Guarded autopilot limits</h3>
        <p className="text-xs text-slate-500 mb-4">
          {authority === "recommend_only"
            ? "These limits apply when you enable Guarded or Managed autopilot."
            : "AI may operate only within these bounds — anything beyond them comes back to you for approval."}
        </p>
        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          {NUM_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">{f.label}</label>
              <div className="flex items-center gap-2">
                <input type="number" min={0} max={100} value={perms[f.key] ?? 0}
                  onChange={(e) => setPerms((p) => ({ ...p, [f.key]: Math.max(0, Math.min(100, parseInt(e.target.value || "0", 10))) }))}
                  data-testid={`autopilot-num-${f.key}`}
                  className="w-28 bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none" />
                <span className="text-sm text-slate-400">{f.suffix}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-2">
          {BOOL_FIELDS.map((f) => (
            <button key={f.key} onClick={() => setPerms((p) => ({ ...p, [f.key]: !p[f.key] }))} data-testid={`autopilot-toggle-${f.key}`}
              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
              <span className="text-sm text-slate-300 text-left">{f.label}</span>
              <span className={`w-9 h-5 rounded-full relative transition-colors shrink-0 ${perms[f.key] ? "bg-cyan-400" : "bg-white/10"}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-paper transition-all ${perms[f.key] ? "left-[18px]" : "left-0.5"}`} />
              </span>
            </button>
          ))}
        </div>
      </Card>

      <div className="flex items-center gap-4">
        <Btn onClick={save} disabled={saving} data-testid="autopilot-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save autopilot settings
        </Btn>
        <button onClick={() => navigate("/optimize")} className="text-xs text-slate-500 hover:text-cyan-300 inline-flex items-center gap-1.5">
          Open Optimization Agent <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      <AutopilotActivity />
    </div>
  );
}
